
import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { FiUploadCloud, FiArrowRight } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import Button from '../Button';
import Select from '../Select';
import Api from '../../Api';
import './style.css';

const DB_FIELDS = [
  { value: 'cbo', label: 'CBO' },
  { value: 'name', label: 'Cargo (Nome)' },
  { value: 'description', label: 'Descrição' },
  { value: 'company_ref', label: 'Empresa (ID ou Nome)' },
];

export default function PositionImportModal({ isOpen, onClose, onSuccess, companyId, companies = [] }) {
  const [step, setStep] = useState('upload'); // upload, map
  const [fileData, setFileData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({}); // { headerName: dbField }
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(companyId || '');

  useEffect(() => {
    setSelectedCompanyId(companyId || '');
  }, [companyId]);

  const normalizeString = (str) => {
    if (!str) return '';
    return String(str).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  };

  const findCompany = (companiesList, ref) => {
    if (!ref) return null;
    const normalizedRef = normalizeString(ref);
    const exactId = companiesList.find(c => String(c.id) === String(ref));
    if (exactId) return exactId;
    return companiesList.find(c => {
      const name1 = normalizeString(c.corporate_name);
      const name2 = normalizeString(c.trade_name);
      return (name1 && (name1.includes(normalizedRef) || normalizedRef.includes(name1))) ||
             (name2 && (name2.includes(normalizedRef) || normalizedRef.includes(name2)));
    }) || null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 }); // Array of arrays

        if (data.length < 2) {
          toast.warning('O arquivo parece estar vazio ou sem dados.');
          return;
        }

        const fileHeaders = data[0];
        const rows = data.slice(1);

        setHeaders(fileHeaders);
        setFileData(rows);
        
        // Auto-map based on similar names
        const initialMapping = {};
        fileHeaders.forEach(header => {
          const lowerHeader = String(header).toLowerCase();
          if (lowerHeader.includes('cbo')) initialMapping[header] = 'cbo';
          else if (lowerHeader.includes('cargo') || lowerHeader.includes('nome')) initialMapping[header] = 'name';
          else if (lowerHeader.includes('descricao') || lowerHeader.includes('descrição')) initialMapping[header] = 'description';
          else if (lowerHeader.includes('empresa') || lowerHeader.includes('company')) initialMapping[header] = 'company_ref';
        });
        
        setMapping(initialMapping);
        setStep('map');
      } catch (error) {
        console.error(error);
        toast.error('Erro ao ler o arquivo.');
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleMappingChange = (header, dbField) => {
    setMapping(prev => ({ ...prev, [header]: dbField }));
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      
      // Transform data based on mapping
      const positionsRaw = fileData.map(row => {
        const position = {};
        let rowCompanyId = selectedCompanyId;
        
        headers.forEach((header, index) => {
          const dbField = mapping[header];
          if (dbField) {
            let value = row[index];
            if (value !== undefined && value !== null) {
                // Ensure strings
                value = String(value).trim();
            }
            if (dbField === 'company_ref') {
              const matchedCompany = findCompany(companies, value);
              if (matchedCompany) rowCompanyId = matchedCompany.id;
              else if (!isNaN(Number(value))) rowCompanyId = Number(value);
            } else {
              position[dbField] = value;
            }
          }
        });

        position.company_id = rowCompanyId;
        return position;
      }).filter(p => p.name); // Ensure name exists (Cargo is required)

      if (positionsRaw.length === 0) {
        toast.warning('Nenhum dado válido para importar. Certifique-se de mapear a coluna "Cargo".');
        setLoading(false);
        return;
      }

      const byCompany = positionsRaw.reduce((acc, item) => {
        if (!acc[item.company_id]) acc[item.company_id] = [];
        acc[item.company_id].push(item);
        return acc;
      }, {});

      let total = 0;
      for (const cId of Object.keys(byCompany)) {
        const created = await Api.importPositions(cId, byCompany[cId]);
        if (Array.isArray(created)) total += created.length;
        else total += byCompany[cId].length;
      }
      
      toast.success(`${total} cargos processados com sucesso!`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao importar dados. Verifique se o formato está correto.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('upload');
    setFileData([]);
    setHeaders([]);
    setMapping({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { reset(); onClose(); }}
      title="Importar Cargos"
      size="lg"
    >
      <div className="import-modal-body">
        <div className="import-content-area">
            {step === 'upload' && (
                <div className="upload-step-container">
                    {companies.length > 0 && (
                       <div className="company-select-container" style={{ marginBottom: '1rem' }}>
                           <Select
                               label="Selecione a empresa padrão para importação"
                               options={[{ value: '', label: 'SELECIONE...' }, ...companies.map(c => ({ value: c.id, label: c.corporate_name || c.trade_name }))]}
                               value={selectedCompanyId}
                               onChange={e => setSelectedCompanyId(e.target.value)}
                               searchable
                           />
                       </div>
                    )}
                <div 
                    className="upload-area"
                    onClick={() => {
                        fileInputRef.current.click();
                    }}
                >
                    <FiUploadCloud className="upload-icon" />
                    <p className="upload-text">
                        Clique para selecionar um arquivo Excel (.xlsx) ou CSV
                    </p>
                    <>
                        <p className="upload-subtext">O arquivo deve conter cabeçalhos na primeira linha.</p>
                        <p className="upload-subtext">Exemplo: Empresa | CBO | Cargo | Descrição</p>
                    </>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept=".xlsx, .xls, .csv"
                        style={{ display: 'none' }}
                    />
                </div>
                </div>
            )}

            {step === 'map' && (
                <div className="map-step">
                    <p className="step-description">Mapeie as colunas do seu arquivo para os campos do sistema.</p>
                    
                    <div className="mapping-grid">
                        {headers.map((header, idx) => (
                            <div key={idx} className="mapping-row">
                                <div className="file-header">
                                    <span className="label">Coluna do Arquivo:</span>
                                    <strong>{header}</strong>
                                    {fileData[0] && (
                                        <span className="example-value">Ex: {fileData[0][idx]}</span>
                                    )}
                                </div>
                                <div className="arrow-icon">
                                    <FiArrowRight />
                                </div>
                                <div className="db-field-select">
                                    <Select 
                                        name={`map-${idx}`}
                                        value={mapping[header] || ''}
                                        onChange={(e) => handleMappingChange(header, e.target.value)}
                                        options={[
                                            { value: '', label: 'Ignorar' },
                                            ...DB_FIELDS
                                        ]}
                                        placeholder="Selecione o campo..."
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>

        <div className="modal-actions">
          <Button onClick={() => step === 'upload' ? onClose() : reset()} className="cancel-button" disabled={loading}>
            {step === 'upload' ? 'Cancelar' : 'Voltar'}
          </Button>
          
          {step === 'map' && (
            <Button onClick={handleImport} disabled={loading}>
              {loading ? 'Importando...' : 'Importar Dados'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
